import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Loader2, AlertTriangle, RefreshCw, Users, UserPlus, Building2, MoreVertical, Trash2, Store, KeyRound } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  getCurrentUserOrganization,
  updateOrganization
} from '@/app/services/organizationService';
import { Organization, OrganizationUpdate } from '@/app/types';
import OrganizationForm from '@/app/components/forms/OrganizationForm';
import { useAuthStore } from '@/app/store/authStore';
import { Link } from 'react-router-dom';
import { UserProfile } from '@/app/types/auth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import UserManagementModal from '@/app/components/forms/UserManagementModal';
import SiteAssignmentModal from '@/app/components/forms/SiteAssignmentModal';
import PartyAssignmentModal from '@/app/components/forms/PartyAssignmentModal';
import ResetPinDialog from '@/app/components/forms/ResetPinDialog';
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  assignUserToSite,
  removeUserFromSite,
  resetUserPin,
  UserWithSites,
  UserCreate,
  UserUpdate,
  ResetUserPinResponse,
} from '@/app/services/userManagementService';
import {
  assignUserToParty,
  unassignUserFromParty,
} from '@/app/services/partyService';

// Define selectors outside component to prevent recreation on each render
const selectUser = (state: { user: UserProfile | null }): UserProfile | null => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }): () => void => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }): boolean => state.isLoading;

const OrganizationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [isPartyModalOpen, setIsPartyModalOpen] = useState(false);
  const [isResetPinDialogOpen, setIsResetPinDialogOpen] = useState(false);
  const [isResetPinConfirmOpen, setIsResetPinConfirmOpen] = useState(false);
  const [userToResetPin, setUserToResetPin] = useState<UserWithSites | null>(null);
  const [currentUser, setCurrentUser] = useState<UserWithSites | null>(null);
  const [userFormMode, setUserFormMode] = useState<'create' | 'edit'>('create');
  const [resetPinCredentials, setResetPinCredentials] = useState<{username: string; pin: string} | null>(null);
  
  // Use individual selectors instead of an object selector to prevent unnecessary rerenders
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Check for organization ID in either organization_id property or nested organization.id
  // This handles different API response structures
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  
  // Use the embedded organization data if available
  const userOrgData = useMemo(() => user?.organization, [user]);
  
  const isOrgAdmin = user?.role?.name === 'org_admin';

  // For debugging
  useEffect(() => {
    console.log('OrganizationsPage mounted/user updated:', user);
    console.log('User organization object:', user?.organization);
    console.log('User organization ID (direct):', user?.organization_id);
    console.log('User organization ID (nested):', user?.organization?.id);
    console.log('Using organization ID:', userOrgId);
  }, [user, userOrgId]);

  // Pre-populate the query cache with organization data from user object if available
  useEffect(() => {
    if (userOrgId && userOrgData && !queryClient.getQueryData(['currentUserOrganization', userOrgId])) {
      console.log('Pre-populating organization data from user object:', userOrgData);
      // Convert UserOrganization to Organization type if needed
      const orgData: Organization = {
        id: userOrgData.id,
        name: userOrgData.name,
        is_active: userOrgData.is_active,
        created_at: userOrgData.created_at,
        updated_at: userOrgData.updated_at
      };
      queryClient.setQueryData(['currentUserOrganization', userOrgId], orgData);
    }
  }, [userOrgId, userOrgData, queryClient]);
  
  // Query to fetch the user's organization
  const { 
    data: organization, 
    isLoading: isOrgLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['currentUserOrganization', userOrgId],
    queryFn: () => {
      console.log('Executing query function with orgId:', userOrgId);
      if (!userOrgId) {
        throw new Error('No organization ID available for query.');
      }
      return getCurrentUserOrganization(userOrgId);
    },
    enabled: !!userOrgId && !userOrgData, // Only run if we need to fetch organization data
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false, // Optional: disable refetch on window focus
  });

  // Log errors for debugging
  useEffect(() => {
    if (isError) {
      console.error('Error fetching organization:', error);
    }
  }, [isError, error]);

  // Get organization data either from the query result or from user object
  const organizationData = useMemo(() => {
    if (organization) return organization;
    if (userOrgData) {
      // Convert UserOrganization to Organization if needed
      return {
        id: userOrgData.id,
        name: userOrgData.name,
        is_active: userOrgData.is_active,
        created_at: userOrgData.created_at,
        updated_at: userOrgData.updated_at
      } as Organization;
    }
    return null;
  }, [organization, userOrgData]);

  // Mutation for updating the organization
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrganizationUpdate }) => updateOrganization(id, data),
    onSuccess: (updatedOrg) => {
      toast.success('Organization updated successfully');
      // Update the query cache directly for immediate UI update
      queryClient.setQueryData(['currentUserOrganization', userOrgId], updatedOrg);
      setIsFormOpen(false);
      setCurrentOrganization(null); // Clear editing state
    },
    onError: (error) => {
      toast.error(`Failed to update organization: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const handleEditClick = (org: Organization): void => {
    setCurrentOrganization(org);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (formData: OrganizationUpdate): void => {
    if (currentOrganization) {
      updateMutation.mutate({ id: currentOrganization.id, data: formData });
    }
  };

  const handleManualRefresh = useCallback((): void => {
    if (userOrgId) {
      console.log("Manually refreshing organization data...");
      refetch();
    }
  }, [userOrgId, refetch]);

  // --- Render Logic ---

  // Show loading indicator if auth state is loading or org query is loading (and not skipped)
  if (isAuthLoading || (isOrgLoading && !!userOrgId && !userOrgData)) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Handle case where user has no assigned organization ID AFTER auth loading is complete
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          Your user account is not associated with an organization. Please contact support or your administrator.
        </p>
        <Button onClick={logoutAction} variant="outline" className="mt-4">
          Logout
        </Button>
      </div>
    );
  }

  // Handle query error state AFTER loading is complete and userOrgId exists
  if (isError && !!userOrgId && !organizationData) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Organization</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={handleManualRefresh} variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  // Handle case where query succeeded but returned no data
  if (!organizationData && !isOrgLoading && !isError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-warning" />
        <p className="text-xl font-semibold">Organization Not Found</p>
        <p className="text-sm text-muted-foreground">
          The organization associated with your account could not be found.
        </p>
        <Button onClick={handleManualRefresh} variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh Data
        </Button>
        <Button onClick={logoutAction} variant="destructive" size="sm" className="mt-2">
          Logout
        </Button>
      </div>
    );
  }

  // Query to fetch users
  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['organizationUsers', userOrgId],
    queryFn: () => listUsers({ limit: 100 }),
    enabled: !!userOrgId && isOrgAdmin,
    staleTime: 30000,
  });

  // Mutation for creating users
  const createUserMutation = useMutation({
    mutationFn: (data: UserCreate) => createUser(data),
    onSuccess: () => {
      toast.success('User created successfully');
      refetchUsers();
      setIsUserModalOpen(false);
      setCurrentUser(null);
    },
    onError: (error) => {
      toast.error(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  // Mutation for updating users
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) => updateUser(id, data),
    onSuccess: () => {
      toast.success('User updated successfully');
      refetchUsers();
      setIsUserModalOpen(false);
      setCurrentUser(null);
    },
    onError: (error) => {
      toast.error(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  // Mutation for deleting users
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      toast.success('User deleted successfully');
      refetchUsers();
    },
    onError: (error) => {
      toast.error(`Failed to delete user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const handleCreateUser = () => {
    setCurrentUser(null);
    setUserFormMode('create');
    setIsUserModalOpen(true);
  };

  const handleEditUser = (user: UserWithSites) => {
    setCurrentUser(user);
    setUserFormMode('edit');
    setIsUserModalOpen(true);
  };

  const handleUserFormSubmit = (data: UserCreate | UserUpdate) => {
    if (userFormMode === 'create' && userOrgId) {
      // Add organization_id to the create data
      const createData: UserCreate = {
        ...(data as UserCreate),
        organization_id: userOrgId
      };
      createUserMutation.mutate(createData);
    } else if (currentUser) {
      updateUserMutation.mutate({ id: currentUser.id, data: data as UserUpdate });
    }
  };

  const handleDeleteUser = (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleManageSites = (user: UserWithSites) => {
    setCurrentUser(user);
    setIsSiteModalOpen(true);
  };

  const handleSiteAssignment = async (siteIds: number[]) => {
    if (!currentUser || !userOrgId) return;

    try {
      // Get current site IDs
      const currentSiteIds = currentUser.sites?.map(s => s.id) || [];

      // Find sites to add and remove
      const sitesToAdd = siteIds.filter(id => !currentSiteIds.includes(id));
      const sitesToRemove = currentSiteIds.filter(id => !siteIds.includes(id));

      // Execute all operations
      await Promise.all([
        ...sitesToAdd.map(siteId => assignUserToSite(siteId, currentUser.id)),
        ...sitesToRemove.map(siteId => removeUserFromSite(siteId, currentUser.id)),
      ]);

      toast.success('Site assignments updated successfully');
      refetchUsers();
    } catch (error) {
      toast.error('Failed to update site assignments');
      throw error;
    }
  };

  const handleManageParties = (user: UserWithSites) => {
    setCurrentUser(user);
    setIsPartyModalOpen(true);
  };

  const handlePartyAssignment = async (partyIds: number[]) => {
    if (!currentUser || !userOrgId) return;

    try {
      // Get current party IDs
      const currentPartyIds = currentUser.managed_parties?.map(p => p.id) || [];

      // Find parties to add and remove
      const partiesToAdd = partyIds.filter(id => !currentPartyIds.includes(id));
      const partiesToRemove = currentPartyIds.filter(id => !partyIds.includes(id));

      // Execute all operations
      await Promise.all([
        ...partiesToAdd.map(partyId => assignUserToParty(partyId, parseInt(currentUser.id))),
        ...partiesToRemove.map(partyId => unassignUserFromParty(partyId, parseInt(currentUser.id))),
      ]);

      toast.success('Party assignments updated successfully');
      refetchUsers();
    } catch (error) {
      toast.error('Failed to update party assignments');
      throw error;
    }
  };

  // Mutation for resetting user PIN
  const resetPinMutation = useMutation({
    mutationFn: (userId: string) => resetUserPin(userId),
    onSuccess: (data: ResetUserPinResponse) => {
      setResetPinCredentials({
        username: data.username,
        pin: data.new_pin,
      });
      setIsResetPinDialogOpen(true);
      toast.success('PIN reset successfully');
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    },
  });

  const handleResetPin = (user: UserWithSites) => {
    setUserToResetPin(user);
    setIsResetPinConfirmOpen(true);
  };

  const confirmResetPin = () => {
    if (userToResetPin) {
      resetPinMutation.mutate(userToResetPin.id);
    }
    setIsResetPinConfirmOpen(false);
    setUserToResetPin(null);
  };

  // --- Display Organization Data ---
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Organization Details</h1>
        <Button onClick={handleManualRefresh} variant="outline" size="sm" disabled={isOrgLoading}>
          {isOrgLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created On</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Render only if organization data is available */}
            {organizationData && (
              <TableRow key={organizationData.id}>
                <TableCell className="font-medium">{organizationData.name}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    organizationData.is_active
                      ? 'border border-success bg-success/10 text-success dark:bg-success/40'
                      : 'border border-warning bg-warning/10 text-warning dark:bg-warning/40'
                  }`}>
                    {organizationData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>{new Date(organizationData.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {isOrgAdmin && (
                      <Button variant="outline" size="sm" onClick={() => handleEditClick(organizationData)}>
                        <Edit className="mr-1 h-4 w-4" />
                        Edit
                      </Button>
                    )}
                    <Link to="/sites">
                      <Button variant="default" size="sm">View Sites</Button>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {/* Show placeholder if data still loading or missing after checks */}
            {!organizationData && !isOrgLoading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                  Organization data could not be loaded.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Users Management Section */}
      {isOrgAdmin && organizationData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  Manage users in your organization
                </CardDescription>
              </div>
              <Button onClick={handleCreateUser} size="sm">
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isUsersLoading ? (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : usersData?.items && usersData.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Sites</TableHead>
                    <TableHead>Parties</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersData.items.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.full_name || user.name || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {user.role?.name || 'No Role'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.sites && user.sites.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span>{user.sites.length}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No sites</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.managed_parties && user.managed_parties.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Store className="h-4 w-4 text-muted-foreground" />
                            <span>{user.managed_parties.length}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No parties</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManageSites(user)}>
                              <Building2 className="mr-2 h-4 w-4" />
                              Manage Sites
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManageParties(user)}>
                              <Store className="mr-2 h-4 w-4" />
                              Manage Parties
                            </DropdownMenuItem>
                            {(user.role?.name === 'client' || user.role?.name === 'field_agent') && (
                              <DropdownMenuItem onClick={() => handleResetPin(user)}>
                                <KeyRound className="mr-2 h-4 w-4" />
                                Reset PIN
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No users found. Click "Add User" to create your first user.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Organization Form Dialog */}
      {currentOrganization && (
        <OrganizationForm
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open);
            if (!open) setCurrentOrganization(null);
          }}
          onSubmit={handleFormSubmit}
          defaultValues={currentOrganization}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}

      {/* User Management Modal */}
      <UserManagementModal
        open={isUserModalOpen}
        onOpenChange={(open) => {
          setIsUserModalOpen(open);
          if (!open) {
            setCurrentUser(null);
            setUserFormMode('create');
          }
        }}
        onSubmit={handleUserFormSubmit}
        defaultValues={currentUser || undefined}
        isLoading={createUserMutation.isPending || updateUserMutation.isPending}
        mode={userFormMode}
      />

      {/* Site Assignment Modal */}
      <SiteAssignmentModal
        open={isSiteModalOpen}
        onOpenChange={(open) => {
          setIsSiteModalOpen(open);
          if (!open) setCurrentUser(null);
        }}
        user={currentUser}
        onAssignSites={handleSiteAssignment}
        organizationId={userOrgId!}
      />

      {/* Party Assignment Modal */}
      <PartyAssignmentModal
        open={isPartyModalOpen}
        onOpenChange={(open) => {
          setIsPartyModalOpen(open);
          if (!open) setCurrentUser(null);
        }}
        user={currentUser}
        onAssignParties={handlePartyAssignment}
        organizationId={userOrgId!}
      />

      {/* Reset PIN Confirmation Dialog */}
      <AlertDialog open={isResetPinConfirmOpen} onOpenChange={setIsResetPinConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset PIN</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset the PIN for{' '}
              <strong>{userToResetPin?.full_name || userToResetPin?.username}</strong>?
              <br />
              <br />
              A new 4-digit PIN will be generated and the old PIN will no longer work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToResetPin(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmResetPin}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset PIN Dialog */}
      <ResetPinDialog
        open={isResetPinDialogOpen}
        onOpenChange={(open) => {
          setIsResetPinDialogOpen(open);
          if (!open) setResetPinCredentials(null);
        }}
        credentials={resetPinCredentials}
      />
    </div>
  );
};

export default OrganizationsPage; 